import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api';
import { t } from '../i18n';
import { Feld, Fehler } from '../components/ui';

type Phase = 'login' | 'mfa' | 'mfaSetup';

export function LoginPage({ onLoggedIn }: { onLoggedIn: () => Promise<unknown> }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [phase, setPhase] = useState<Phase>('login');
  const [qr, setQr] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api
      .get<{ needsSetup: boolean }>('/api/v1/auth/setup-status')
      .then((s) => setNeedsSetup(s.needsSetup))
      .catch(() => undefined);
  }, []);

  async function submitLogin(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await api.post<{ status: string }>('/api/v1/auth/login', { email, password });
      if (res.status === 'OK') {
        await onLoggedIn();
        navigate('/', { replace: true });
      } else if (res.status === 'MFA_REQUIRED') {
        setPhase('mfa');
      } else {
        const setup = await api.post<{ qrDataUrl: string }>('/api/v1/auth/mfa/setup');
        setQr(setup.qrDataUrl);
        setPhase('mfaSetup');
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.code : 'INTERNAL');
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const url = phase === 'mfa' ? '/api/v1/auth/mfa/verify' : '/api/v1/auth/mfa/enable';
      await api.post(url, { code });
      await onLoggedIn();
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.code : 'INTERNAL');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-rahmen">
      <div className="logo-block">
        <img src="/logo.svg" alt="WerkDelta" />
        <p style={{ color: 'var(--text-schwach)' }}>{t('app.claim')}</p>
      </div>
      <div className="karte">
        {phase === 'login' && (
          <form onSubmit={submitLogin}>
            <h1>{t('auth.login.title')}</h1>
            <Feld label={t('auth.email')} required>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                required
              />
            </Feld>
            <Feld label={t('auth.password')} required>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </Feld>
            <Fehler code={error} />
            <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', margin: '0.4rem 0 0.2rem' }}>
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                style={{ marginTop: '0.25rem' }}
              />
              <span style={{ fontSize: '0.9rem' }}>
                {t('auth.terms.accept')}{' '}
                (<Link to="/nutzungsbedingungen">{t('app.nutzungLink')}</Link>,{' '}
                <Link to="/datenschutz">{t('app.datenschutzLink')}</Link>)
              </span>
            </label>
            <div className="aktionen">
              <button type="submit" className="knopf-primaer" disabled={busy || !accepted}>
                {t('auth.login.action')}
              </button>
              {needsSetup && (
                <Link className="knopf knopf-sekundaer" to="/einrichtung">
                  {t('auth.setup.link')}
                </Link>
              )}
            </div>
            <p className="erklaerung" style={{ marginTop: '0.8rem' }}>
              {t('auth.reset.hint')}
            </p>
          </form>
        )}
        {phase !== 'login' && (
          <form onSubmit={submitCode}>
            <h1>{t('auth.mfa.title')}</h1>
            {phase === 'mfaSetup' && (
              <>
                <p>{t('auth.mfa.setupIntro')}</p>
                {qr && <img src={qr} alt={t('auth.mfa.qrAlt')} width={220} height={220} />}
              </>
            )}
            {phase === 'mfa' && <p>{t('auth.mfa.verifyIntro')}</p>}
            <Feld label={t('auth.mfa.code')} required>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                autoComplete="one-time-code"
                required
              />
            </Feld>
            <Fehler code={error} />
            <div className="aktionen">
              <button type="submit" className="knopf-primaer" disabled={busy}>
                {t('common.confirm')}
              </button>
            </div>
          </form>
        )}
      </div>
      <nav aria-label="Rechtliches" style={{ marginTop: '1.25rem', textAlign: 'center', fontSize: '0.9rem' }}>
        <Link to="/impressum">{t('app.impressumLink')}</Link>
        {' · '}
        <Link to="/nutzungsbedingungen">{t('app.nutzungLink')}</Link>
        {' · '}
        <Link to="/datenschutz">{t('app.datenschutzLink')}</Link>
      </nav>
    </div>
  );
}
